import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Layers, MessageCircle, Users, User } from 'lucide-react';

const navItems = [
  { path: '/', icon: Layers, labelKey: 'nav.circles', activePaths: ['/'] },
  { path: '/messages', icon: MessageCircle, labelKey: 'nav.chats', activePaths: ['/messages', '/chat'] },
  { path: '/friends', icon: Users, labelKey: 'nav.friends', activePaths: ['/friends'] },
  { path: '/profile', icon: User, labelKey: 'nav.me', activePaths: ['/profile'] },
];

/** 沉浸式页（聊天输入区贴底）隐藏主导航，避免遮挡发送按钮 */
function shouldHideBottomNav(pathname) {
  return /(^|\/)chat(\/|$)/.test(pathname || '');
}

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = (item) => {
    return item.activePaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  };

  if (shouldHideBottomNav(location.pathname)) {
    return null;
  }

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 safe-bottom px-3 pb-2 pt-1"
      role="navigation"
      aria-label="主导航"
    >
      <div
        className="mx-auto flex h-[3.75rem] max-w-lg items-center justify-around rounded-[1.35rem] border border-neutral-200/80 dark:border-dark-border bg-white/90 dark:bg-dark-card/90 shadow-nav backdrop-blur-xl"
      >
        {navItems.map(item => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <motion.button
              key={item.path}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate(item.path)}
              aria-label={t(item.labelKey)}
              aria-current={active ? 'page' : undefined}
              className={`relative flex h-full w-full flex-col items-center justify-center gap-0.5 transition-colors duration-200 ${
                active
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-neutral-400 dark:text-dark-muted hover:text-neutral-600 dark:hover:text-dark-text'
              }`}
            >
              {active && (
                <motion.div
                  layoutId="nav-pill"
                  className="absolute inset-x-2 top-1.5 bottom-1.5 rounded-2xl bg-primary-50 dark:bg-primary-500/15"
                  transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                />
              )}
              <Icon
                size={21}
                strokeWidth={active ? 2.4 : 1.8}
                className="relative z-10 transition-all duration-200"
              />
              <span className="relative z-10 text-[11px] font-medium">{t(item.labelKey)}</span>
            </motion.button>
          );
        })}
      </div>
    </nav>
  );
}
